import { type ReactNode } from 'react';

interface PageLayoutProps {
	/**
	 * Right-aligned actions rendered above the filters/body (typically a
	 * primary Create/Issue/Allocate button, sometimes preceded by a filter
	 * or refresh control). Wrap each action in `<Can>` on the caller side.
	 *
	 * When there are no actions AND no filters, this row is skipped entirely
	 * so the body sits flush with the top of MainContent.
	 */
	actions?: ReactNode;
	/**
	 * Optional filter row rendered above the main content. Left-aligned so
	 * filters (Select, Search, etc.) sit opposite the actions on the same
	 * horizontal band. Skip this prop when the page has no filters.
	 */
	filters?: ReactNode;
	/**
	 * Page body — usually a DataTable, sometimes a form or a card grid. Not
	 * wrapped in a Card by default; pages that want a card visual should
	 * import and use `<Card>` explicitly.
	 */
	children: ReactNode;
}

/**
 * PageLayout — the single spacing/layout container for every admin page.
 *
 * NOTE: the page title lives in the Header (see Header.tsx → usePageTitle),
 * NOT here. This component intentionally has no title/subtitle props; adding
 * them would duplicate the header. If a page needs an inline heading (rare —
 * usually only for card grids like the Dashboard), the page renders its own
 * heading and skips PageLayout entirely.
 *
 * Handles:
 *   - Filters (left) + actions (right) on one horizontal band
 *   - Consistent vertical rhythm between filter band and body
 *   - No opinion on the body itself — page decides between DataTable, Card
 *     grid, form, etc.
 *
 * MainContent already applies `p-6` around every route, so this component
 * only manages spacing WITHIN the route. Do not add outer padding here.
 */
export function PageLayout({ actions, filters, children }: PageLayoutProps) {
	const hasControlBand = !!(actions || filters);
	return (
		<div className="flex flex-col gap-4">
			{hasControlBand && (
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-3 min-w-0">{filters}</div>
					{actions && (
						<div className="flex flex-wrap items-center gap-2">{actions}</div>
					)}
				</div>
			)}
			<div className="min-w-0">{children}</div>
		</div>
	);
}
